import { useEffect, useState } from "react";
import { MessageCircle } from "lucide-react";
import {
  SITE,
  SERVICES,
  PROCESS_STEPS,
  instagramLink,
  whatsappLink,
} from "@/lib/site";
import { LIGHTBOX_ORDER, PHOTOS } from "@/lib/photos";
import { FramedImage } from "@/components/FramedImage";
import { Reveal } from "@/components/Reveal";
import { SplitText } from "@/components/SplitText";
import { BeforeAfter } from "@/components/BeforeAfter";
import { Lightbox } from "@/components/Lightbox";
import { Carousel } from "@/components/Carousel";
import { ServiceCard } from "@/components/ServiceCard";
import { ArtSection } from "@/components/ArtSection";
import { Testimonials } from "@/components/Testimonials";
import { BottomNav } from "@/components/BottomNav";
import { SiteHeader } from "@/components/SiteHeader";
import { BookBar } from "@/components/BookBar";
import { ContactSection } from "@/components/ContactForm";
import { InstagramGlyph } from "@/components/icons";
import "./index.css";

const LOOK_PHOTOS = [PHOTOS.look1, PHOTOS.look2, PHOTOS.look3];
const LOOK_LABELS = ["Front", "Three-quarter", "Detail"];

export function App() {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const lightboxOpen = lightboxIndex !== null;
  // Only BookBar still needs this; the header derives its own now.
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const openLightbox = (slug: string) =>
    setLightboxIndex(LIGHTBOX_ORDER.findIndex(p => p.slug === slug));

  return (
    // extra bottom padding on mobile so the fixed tab bar never covers content
    <div className="grain min-h-screen overflow-x-hidden pb-[7.5rem] lg:pb-0">
      <SiteHeader floatUntilScroll />

      <main id="top">
        <section className="relative flex min-h-[100svh] flex-col items-center justify-center overflow-hidden px-6 text-center">
          <div className="pointer-events-none absolute inset-0 -z-10">
            <div className="animate-float-soft absolute top-[12%] left-1/2 h-[34rem] w-[34rem] -translate-x-1/2 rounded-full bg-accent/45 blur-[120px]" />
            <div className="animate-float-soft absolute right-[8%] bottom-[18%] h-56 w-56 rounded-full bg-gold/12 blur-[90px]" style={{ animationDelay: "-3s" }} />
          </div>

          <Reveal className="max-w-3xl" variant="fade">
            <p className="eyebrow animate-fade-in">Debut Work</p>
          </Reveal>

          <SplitText
            as="h1"
            text={SITE.tagline}
            className="mt-7 max-w-4xl font-display text-[clamp(3.5rem,13vw,9rem)] leading-[0.92] font-light tracking-[-0.02em]"
            stagger={90}
          />

          <Reveal className="max-w-3xl" variant="up" delay={420}>
            <div className="mx-auto mt-8 h-px w-16 bg-gold/60" />

            <p className="mx-auto mt-8 max-w-xl font-sans text-sm leading-relaxed font-light text-muted-foreground sm:text-base">
              {SITE.intro}
            </p>

            <div className="mt-11 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <a
                href="#transformation"
                className="inline-flex w-full items-center justify-center rounded-full bg-foreground px-8 py-4 font-sans text-[0.6875rem] tracking-[0.24em] text-background uppercase transition-transform duration-500 hover:-translate-y-0.5 sm:w-auto"
              >
                See the transformation
              </a>
              <a
                href="#look"
                className="inline-flex w-full items-center justify-center rounded-full border border-foreground/25 px-8 py-4 font-sans text-[0.6875rem] tracking-[0.24em] uppercase transition-colors duration-500 hover:border-foreground sm:w-auto"
              >
                View the look
              </a>
            </div>
          </Reveal>

          <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
            <span className="block h-12 w-px overflow-hidden bg-border">
              <span className="block h-1/2 w-full animate-scroll-hint bg-gold" />
            </span>
          </div>
        </section>

        <section id="look" className="scroll-mt-24 px-6 py-24 sm:py-32 lg:px-10">
          <div className="mx-auto max-w-6xl">
            <Reveal className="text-center" variant="up">
              <p className="eyebrow">The Look</p>
              <SplitText
                as="h2"
                text="Every angle, held perfectly"
                className="mt-5 font-display text-[clamp(2.25rem,6vw,4rem)] leading-tight"
              />
            </Reveal>

            <div className="mt-16">
              <Carousel photos={LOOK_PHOTOS} labels={LOOK_LABELS} onOpen={openLightbox} />
            </div>

            <Reveal className="mx-auto mt-20 max-w-xl text-center" delay={120}>
              <div className="mx-auto h-px w-10 bg-gold/60" />
              <h3 className="mt-7 font-display text-[clamp(1.75rem,3.5vw,2.5rem)] leading-snug">
                Base that looks like skin, not makeup
              </h3>
              <p className="mt-5 font-sans text-sm leading-relaxed font-light text-muted-foreground">
                Every layer was built to disappear into the skin — evenness first, colour
                second. The finish is soft, seamless, and made to survive a long day.
              </p>
            </Reveal>
          </div>
        </section>

        <section
          id="transformation"
          className="scroll-mt-24 border-y border-border/60 bg-secondary/40 px-6 py-24 sm:py-32 lg:px-10"
        >
          <div className="mx-auto grid max-w-6xl items-center gap-14 lg:grid-cols-2 lg:gap-20">
            <Reveal>
              <p className="eyebrow">The Transformation</p>
              <h2 className="mt-5 font-display text-[clamp(2.25rem,6vw,4rem)] leading-tight">
                Bare skin, then everything
              </h2>
              <p className="mt-6 max-w-md font-sans text-sm leading-relaxed font-light text-muted-foreground">
                The same face, the same light, the same moment — separated by one hour of
                careful work. Slide across to see exactly what changed.
              </p>

              <dl className="mt-12 space-y-7">
                {PROCESS_STEPS.map(([num, title, detail], i) => (
                  <Reveal key={num} delay={i * 90} variant="left">
                    <div className="flex gap-6 border-t border-border pt-6">
                      <span className="font-display text-sm text-gold">{num}</span>
                      <div>
                        <dt className="font-sans text-[0.8125rem] tracking-[0.12em] uppercase">
                          {title}
                        </dt>
                        <dd className="mt-2 font-sans text-sm leading-relaxed font-light text-muted-foreground">
                          {detail}
                        </dd>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </dl>
            </Reveal>

            <Reveal delay={140} variant="scale">
              <BeforeAfter before={PHOTOS.before} after={PHOTOS.after} />
            </Reveal>
          </div>
        </section>

        <ArtSection />

        <section id="about" className="scroll-mt-24 px-6 py-24 sm:py-32 lg:px-10">
          <div className="mx-auto max-w-3xl text-center">
            <Reveal>
              <p className="eyebrow">The Story</p>
              <h2 className="mt-5 font-display text-[clamp(2.25rem,6vw,4rem)] leading-tight">
                One face. One look. One perfect result.
              </h2>
            </Reveal>

              <Reveal delay={200} className="mt-14 flex flex-col items-center">
                <FramedImage
                  photo={PHOTOS.founder}
                  sizes="(min-width: 640px) 320px, 72vw"
                  ratio="aspect-[4/5]"
                  className="w-[min(20rem,72vw)]"
                />
                <div className="mt-6 flex items-center gap-4">
                  <div className="h-px w-8 bg-gold/60" />
                  <div className="text-center">
                    <p className="font-display text-lg leading-none">{SITE.name}</p>
                    <p className="mt-2 font-sans text-[0.625rem] tracking-[0.24em] text-muted-foreground uppercase">
                      {SITE.role}
                    </p>
                  </div>
                  <div className="h-px w-8 bg-gold/60" />
                </div>
              </Reveal>

            <Reveal delay={120} className="mt-10 space-y-6">
              <SplitText
                as="p"
                text="This was my very first full makeup — the one I had been practising in my head for a long time."
                className="font-display text-xl leading-relaxed font-light text-foreground/85 sm:text-2xl"
                stagger={18}
              />
              <Reveal variant="up" delay={140}>
                <p className="font-sans text-sm leading-relaxed font-light text-muted-foreground sm:text-base">
                  I did it slowly, and I did not rush a single step. The result surprised me:
                  the skin still looked like skin, the features still looked like hers, and
                  somehow the whole thing just worked. Nothing was heavy. Nothing was borrowed
                  from anyone else. It was simply right.
                </p>
              </Reveal>
              <Reveal variant="up" delay={220}>
                <p className="font-sans text-sm leading-relaxed font-light text-muted-foreground sm:text-base">
                  That is the look I want to make for everyone who sits in my chair — polished,
                  photograph well, and still unmistakably themselves.
                </p>
              </Reveal>
            </Reveal>
          </div>
        </section>

        <Testimonials />

        <section
          id="services"
          className="scroll-mt-24 border-t border-border/60 px-6 py-24 sm:py-32 lg:px-10"
        >
          <div className="mx-auto max-w-6xl">
            <Reveal>
              <p className="eyebrow text-center">Services</p>
              <SplitText
                as="h2"
                text="What I do"
                className="mt-5 text-center font-display text-[clamp(2.25rem,6vw,4rem)] leading-tight"
              />
              <p className="mx-auto mt-6 max-w-md text-center font-sans text-sm leading-relaxed font-light text-muted-foreground">
                Tap any look to see what&rsquo;s included, then message me to check availability.
              </p>
            </Reveal>

            <div className="mt-16 grid grid-cols-1 gap-x-16 md:grid-cols-2">
              {SERVICES.map((service, i) => (
                <Reveal key={service.name} delay={(i % 2) * 110} variant="up">
                  <ServiceCard service={service} index={i} defaultOpen={i === 0} />
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <ContactSection />

        <section
          id="book"
          className="scroll-mt-24 border-t border-border/60 px-6 py-24 sm:py-32 lg:px-10"
        >
          <Reveal className="mx-auto max-w-3xl text-center">
            <p className="eyebrow">Book a session</p>
            <SplitText
              as="h2"
              text="Let's make your look"
              className="mt-5 font-display text-[clamp(2.5rem,7vw,5rem)] leading-[1.05]"
              stagger={45}
            />
            <p className="mx-auto mt-7 max-w-lg font-sans text-sm leading-relaxed font-light text-muted-foreground sm:text-base">
              Tell me the occasion and the date. I&apos;ll get back to you with availability
              and a plan for your look.
            </p>

            <div className="mt-11 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <a
                href={whatsappLink()}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-foreground px-9 py-4 font-sans text-[0.6875rem] tracking-[0.24em] text-background uppercase transition-transform duration-500 hover:-translate-y-0.5 sm:w-auto"
              >
                <MessageCircle className="h-4 w-4" strokeWidth={1.5} />
                Message on WhatsApp
              </a>
              {SITE.instagramHandle && (
                <a
                  href={instagramLink(SITE.instagramHandle)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex w-full items-center justify-center gap-2.5 rounded-full border border-foreground/25 px-9 py-4 font-sans text-[0.6875rem] tracking-[0.24em] uppercase transition-colors duration-500 hover:border-foreground sm:w-auto"
                >
                  <InstagramGlyph className="h-4 w-4" />
                  Instagram
                </a>
              )}
            </div>

            <p className="mt-8 font-sans text-[0.6875rem] tracking-[0.2em] text-muted-foreground uppercase">
              {SITE.location}
            </p>

            <a
              href="#contact"
              className="mt-4 inline-block font-sans text-[0.625rem] tracking-[0.2em] text-muted-foreground uppercase transition-colors hover:text-gold link-underline"
            >
              Prefer a form? Send an enquiry
            </a>
          </Reveal>
        </section>
      </main>

      <footer className="border-t border-border/60 px-6 py-12 lg:px-10">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-6 sm:flex-row">
          <div className="text-center sm:text-left">
            <p className="font-display text-base tracking-[0.2em] uppercase">{SITE.name}</p>
            <p className="mt-1.5 font-sans text-[0.625rem] tracking-[0.28em] text-muted-foreground uppercase">
              {SITE.role}
            </p>
          </div>

          <p className="font-sans text-[0.625rem] tracking-[0.2em] text-muted-foreground uppercase">
            &copy; {new Date().getFullYear()} {SITE.name}. All rights reserved.
          </p>

          <a
            href="/admin"
            className="font-sans text-[0.625rem] tracking-[0.2em] text-muted-foreground/50 uppercase transition-colors hover:text-gold"
          >
            Enquiries
          </a>
        </div>
      </footer>

      <Lightbox
        photos={LIGHTBOX_ORDER}
        index={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onIndexChange={setLightboxIndex}
      />

      <BottomNav hidden={lightboxOpen} />
      <BookBar hidden={lightboxOpen || !scrolled} />
    </div>
  );
}

export default App;
